import { useState, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useParams } from "@tanstack/react-router";
import { Plus, Pencil, Trash2, BadgePercent, Search, X } from "lucide-react";
import { InlineLoadError } from "@/components/feedback/InlineLoadError";
import {
  useQuotation,
  useQuotationItems,
  useCreateQuotationItem,
  useUpdateQuotationItem,
  useDeleteQuotationItem,
  useUpdateQuotation,
} from "@/features/quotation/hooks";
import { useSupplierQuotationSubmissions } from "@/features/supplierQuotation/hooks";
import type {
  EnrichedQuotationItem,
  UnitType,
  CreateItemPayload,
  UpdateItemPayload,
} from "@/features/quotation/types";
import type { SupplierQuotationSubmission } from "@/features/supplierQuotation/types";
import {
  buildUnitPricePreview,
  isDirectKgUnit,
  usesKgPerBox,
  usesUnitsPerBox,
} from "@/features/quotation/unitLabels";
import {
  buildEffectiveQuotedKeys,
  buildEffectiveItems,
  buildStableGrouped,
  reconcileOptimisticState,
} from "@/features/quotation/optimistic";
import { buildCreateQuotationItemPayloadFromSubmission } from "@/features/quotation/payloads";
import { useProducts, useCreateProduct } from "@/features/product/hooks";
import { useSuppliers, useCreateSupplier } from "@/features/supplier/hooks";
import type {
  Product,
  ProductCategory,
  ProductUnit,
} from "@/features/product/types";
import type { Supplier } from "@/features/supplier/types";
import { formatMoneyInput, parseMoneyInput } from "@/lib/timeAgo";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { getOperationalCapabilities } from "@/features/auth/operationalCapabilities";
import { useFeatureFlags } from "@/features/featureFlags/hooks";
import { QuotationExportModal } from "@/components/quotation/QuotationExportModal";
import { QuotationDetailFilterPopover } from "@/components/quotation/QuotationDetailFilterPopover";
import {
  EMPTY_QUOTATION_DETAIL_FILTERS,
  matchesQuotationDetailFilters,
  type QuotationDetailFilters,
} from "@/features/quotation/detailFilters";
import { QuotationAutocompleteField } from "./QuotationAutocompleteField";
import { QuotationDrawerFooter } from "./QuotationDrawerFooter";
import {
  QuotationPricePreview,
  QuotationPriceSection,
  QuotationSupplierChangeWarning,
  QuotationUnitSection,
} from "./QuotationDrawerSections";
import { QuotationDetailHeader } from "./QuotationDetailHeader";
import {
  QuotationDetailLedgerRow,
  QuotationItemRemoveConfirmation,
  QuotationOfferButton,
} from "./QuotationDetailLedger";
import {
  InlineCreateProductPanel,
  InlineCreateSupplierPanel,
} from "./QuotationInlineCreatePanels";
import { QuotationItemDrawerFields } from "./QuotationItemDrawerFields";
import { QuotationItemEditor } from "./QuotationItemEditor";
import {
  EXIT_ANIMATION_MS,
  getQuotationItemValidationError,
  mapProductDefaultUnitToQuotationUnit,
} from "./quotationDetailShared";

const CATEGORY_ORDER = [
  "LEGUMES",
  "FRUTAS",
  "VERDURAS",
  "GRAOS",
  "TEMPEROS",
  "OVOS",
  "OUTROS",
] as const;

const CATEGORY_LABELS: Record<string, string> = {
  LEGUMES: "Legumes",
  FRUTAS: "Frutas",
  VERDURAS: "Verduras",
  GRAOS: "Grãos",
  TEMPEROS: "Temperos",
  OVOS: "Ovos",
  OUTROS: "Diversos",
};

function getOfficialActionError(error: unknown, fallback: string) {
  return getUserFacingErrorMessage(error, fallback);
}

interface QuotationDetailOfficialPageProps {
  topSlot?: ReactNode;
}

export function QuotationDetailOfficialPage({
  topSlot,
}: QuotationDetailOfficialPageProps = {}) {
  const params = useParams({ strict: false }) as { quotationId?: string };
  const quotationId = params.quotationId ?? "";
  const { data: currentUser } = useCurrentUser();
  const isSuper = currentUser?.role === "super";
  const { canManageQuotation, canRemoveQuotationItem } =
    getOperationalCapabilities(currentUser);
  const { data: featureFlags } = useFeatureFlags(isSuper);

  const { data: quotation } = useQuotation(quotationId);
  const itemsQuery = useQuotationItems(quotationId);
  const itemsData = itemsQuery.data;
  const submissionsQuery = useSupplierQuotationSubmissions(
    quotation?.date ?? "",
  );
  const submissionsData = submissionsQuery.data;
  const itemsReady = itemsQuery.isSuccess && !!itemsData;
  const submissionsReady = submissionsQuery.isSuccess && !!submissionsData;
  const ledgerReady = itemsReady && submissionsReady;
  const itemsActionsReady =
    ledgerReady && !itemsQuery.isFetching && !submissionsQuery.isFetching;
  const itemsLoading = !itemsReady && !itemsQuery.isError;
  const submissionsLoading = !submissionsReady && !submissionsQuery.isError;
  const { data: productsData } = useProducts();
  const { data: suppliersData } = useSuppliers();
  const createItemMutation = useCreateQuotationItem(quotationId);
  const updateItemMutation = useUpdateQuotationItem(quotationId);
  const deleteItemMutation = useDeleteQuotationItem(quotationId);
  const createProductMutation = useCreateProduct();
  const createSupplierMutation = useCreateSupplier();

  const updateQuotationMutation = useUpdateQuotation();
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<QuotationDetailFilters>(
    EMPTY_QUOTATION_DETAIL_FILTERS,
  );

  // Category expand state
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(CATEGORY_ORDER),
  );

  // Drawer state
  const [drawer, setDrawer] = useState<{
    mode: "create" | "edit";
    item?: EnrichedQuotationItem;
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const lastRemoveIdRef = useRef<string | null>(null);
  const removeTriggerRef = useRef<HTMLButtonElement>(null);
  const confirmRemoveRef = useRef<HTMLButtonElement>(null);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // Product autocomplete
  const [productSearch, setProductSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [showInlineProduct, setShowInlineProduct] = useState(false);
  const [inlineProductName, setInlineProductName] = useState("");
  const [inlineProductCategory, setInlineProductCategory] =
    useState<ProductCategory>("LEGUMES");
  const [inlineProductUnit, setInlineProductUnit] = useState<ProductUnit>("kg");

  // Supplier autocomplete
  const [supplierSearch, setSupplierSearch] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
    null,
  );
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [showInlineSupplier, setShowInlineSupplier] = useState(false);
  const [inlineSupplierName, setInlineSupplierName] = useState("");

  // Pricing
  const [pricePerBox, setPricePerBox] = useState("");
  const [pricePerKg, setPricePerKg] = useState("");
  const [unitType, setUnitType] = useState<UnitType>("cx_kg");
  const [kgPerBox, setKgPerBox] = useState("");
  const [unitsPerBox, setUnitsPerBox] = useState("");
  const [isOffer, setIsOffer] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const itemSubmitInFlightRef = useRef(false);

  function applyUnitType(nextUnitType: UnitType) {
    setUnitType(nextUnitType);
    if (!isDirectKgUnit(nextUnitType)) setPricePerKg("");
    if (!usesKgPerBox(nextUnitType)) setKgPerBox("");
    if (!usesUnitsPerBox(nextUnitType)) setUnitsPerBox("");
    if (isDirectKgUnit(nextUnitType)) setPricePerBox("");
  }

  const [pendingSelections, setPendingSelections] = useState<Set<string>>(
    new Set(),
  );
  const [pendingRemovals, setPendingRemovals] = useState<Set<string>>(
    new Set(),
  );
  const [pendingDeselections, setPendingDeselections] = useState<Set<string>>(
    new Set(),
  );
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (confirmRemoveId) confirmRemoveRef.current?.focus();
    else if (lastRemoveIdRef.current) removeTriggerRef.current?.focus();
  }, [confirmRemoveId]);

  const isMountedRef = useRef(true);
  const itemsActionsReadyRef = useRef(itemsActionsReady);
  itemsActionsReadyRef.current = itemsActionsReady;
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  async function handleReopen() {
    if (!quotation) return;
    try {
      await updateQuotationMutation.mutateAsync({
        id: quotation.id,
        payload: { status: "in_progress" },
      });
    } catch (error) {
      setActionError(
        getOfficialActionError(
          error,
          "Não foi possível reabrir a cotação. Tente novamente.",
        ),
      );
    }
  }

  function resetForm() {
    setProductSearch("");
    setSelectedProduct(null);
    setShowProductDropdown(false);
    setShowInlineProduct(false);
    setInlineProductName("");
    setInlineProductCategory("LEGUMES");
    setInlineProductUnit("kg");
    setSupplierSearch("");
    setSelectedSupplier(null);
    setShowSupplierDropdown(false);
    setShowInlineSupplier(false);
    setInlineSupplierName("");
    setPricePerBox("");
    setPricePerKg("");
    setUnitType("cx_kg");
    setKgPerBox("");
    setUnitsPerBox("");
    setIsOffer(false);
    setValidationAttempted(false);
    setConfirmDelete(false);
  }

  function openCreate() {
    if (!canManageQuotation || isCompleted || !itemsActionsReady) return;
    resetForm();
    setDrawer({ mode: "create" });
  }

  function openCreateForProduct(product: Product) {
    if (!canManageQuotation || isCompleted || !itemsActionsReady) return;
    resetForm();
    setSelectedProduct(product);
    setProductSearch(product.name);
    setUnitType(mapProductDefaultUnitToQuotationUnit(product.default_unit));
    setDrawer({ mode: "create" });
  }

  function openEdit(item: EnrichedQuotationItem) {
    if (!canManageQuotation || isCompleted || !itemsActionsReady) return;
    resetForm();
    const products = productsData?.items ?? [];
    const suppliers = suppliersData?.items ?? [];
    const product = products.find((p) => p.id === item.product_id) ?? null;
    const supplier = suppliers.find((s) => s.id === item.supplier_id) ?? null;
    setProductSearch(item.product_name);
    setSelectedProduct(product);
    setSupplierSearch(item.supplier_name);
    setSelectedSupplier(supplier);
    setPricePerBox(
      item.price_per_box != null ? formatMoneyInput(item.price_per_box) : "",
    );
    setPricePerKg(
      item.price_per_kg != null ? formatMoneyInput(item.price_per_kg) : "",
    );
    setUnitType(item.unit_type);
    setKgPerBox(item.kg_per_box != null ? String(item.kg_per_box) : "");
    setUnitsPerBox(
      item.units_per_box != null ? String(item.units_per_box) : "",
    );
    setIsOffer(item.is_offer);
    setDrawer({ mode: "edit", item });
  }

  function closeDrawer() {
    if (itemSubmitInFlightRef.current || isPending) return;
    finishClose();
  }

  function finishClose() {
    setDrawer(null);
    setDrawerError(null);
    resetForm();
  }

  function toggleCategory(cat: string) {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }

  const filteredProducts = useMemo(() => {
    const all = productsData?.items ?? [];
    if (!productSearch) return all.slice(0, 8);
    const q = productSearch.toLowerCase();
    return all.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 8);
  }, [productsData, productSearch]);

  const filteredSuppliers = useMemo(() => {
    const all = suppliersData?.items ?? [];
    if (!supplierSearch) return all.slice(0, 8);
    const q = supplierSearch.toLowerCase();
    return all.filter((s) => s.name.toLowerCase().includes(q)).slice(0, 8);
  }, [suppliersData, supplierSearch]);

  const pricePreview = useMemo(() => {
    return buildUnitPricePreview({
      unitType,
      pricePerBox: parseMoneyInput(pricePerBox),
      pricePerKg: parseMoneyInput(pricePerKg),
      kgPerBox: kgPerBox ? parseFloat(kgPerBox) : null,
      unitsPerBox: unitsPerBox ? parseFloat(unitsPerBox) : null,
    });
  }, [pricePerBox, pricePerKg, unitType, kgPerBox, unitsPerBox]);
  const itemValidationError = getQuotationItemValidationError({
    unitType,
    pricePerBox,
    pricePerKg,
    kgPerBox,
    unitsPerBox,
  });

  async function handleCreateInlineProduct() {
    if (!itemsActionsReady || !inlineProductName.trim()) return;
    setDrawerError(null);
    try {
      const product = await createProductMutation.mutateAsync({
        name: inlineProductName.trim(),
        category: inlineProductCategory,
        default_unit: inlineProductUnit,
      });
      setSelectedProduct(product);
      setProductSearch(product.name);
      setShowInlineProduct(false);
      setInlineProductName("");
    } catch (e) {
      setDrawerError(
        getOfficialActionError(
          e,
          "Não foi possível criar o produto. Tente novamente.",
        ),
      );
    }
  }

  async function handleCreateInlineSupplier() {
    if (!itemsActionsReady || !inlineSupplierName.trim()) return;
    setDrawerError(null);
    try {
      const supplier = await createSupplierMutation.mutateAsync({
        name: inlineSupplierName.trim(),
      });
      setSelectedSupplier(supplier);
      setSupplierSearch(supplier.name);
      setShowInlineSupplier(false);
      setInlineSupplierName("");
    } catch (e) {
      setDrawerError(
        getOfficialActionError(
          e,
          "Não foi possível criar o fornecedor. Tente novamente.",
        ),
      );
    }
  }

  async function handleSubmit() {
    if (
      itemSubmitInFlightRef.current ||
      !drawer ||
      !canManageQuotation ||
      !itemsActionsReady
    )
      return;
    if (itemValidationError) {
      setValidationAttempted(true);
      return;
    }
    if (!selectedProduct || !selectedSupplier) return;
    const parsedPricePerBox = parseMoneyInput(pricePerBox);
    const parsedPricePerKg = parseMoneyInput(pricePerKg);

    const payload: CreateItemPayload = {
      product_id: selectedProduct.id,
      supplier_id: selectedSupplier.id,
      unit_type: unitType,
      is_offer: isOffer,
    };

    if (isDirectKgUnit(unitType)) {
      payload.price_per_kg = parsedPricePerKg!;
    } else {
      payload.price_per_box = parsedPricePerBox!;
    }

    if (usesKgPerBox(unitType) && kgPerBox) {
      payload.kg_per_box = parseFloat(kgPerBox);
    }
    if (usesUnitsPerBox(unitType) && unitsPerBox) {
      payload.units_per_box = parseFloat(unitsPerBox);
    }

    itemSubmitInFlightRef.current = true;
    setDrawerError(null);
    try {
      if (drawer?.mode === "create") {
        await createItemMutation.mutateAsync(payload);
      } else if (drawer?.mode === "edit" && drawer.item) {
        const updatePayload: UpdateItemPayload = {
          unit_type: unitType,
          is_offer: isOffer,
        };
        if (isDirectKgUnit(unitType)) {
          updatePayload.price_per_kg = parsedPricePerKg!;
          updatePayload.price_per_box = undefined;
        } else {
          updatePayload.price_per_box = parsedPricePerBox!;
          updatePayload.price_per_kg = undefined;
        }
        if (usesKgPerBox(unitType))
          updatePayload.kg_per_box = kgPerBox
            ? parseFloat(kgPerBox)
            : undefined;
        if (usesUnitsPerBox(unitType))
          updatePayload.units_per_box = unitsPerBox
            ? parseFloat(unitsPerBox)
            : undefined;
        if (
          selectedSupplier &&
          selectedSupplier.id !== drawer.item.supplier_id
        ) {
          updatePayload.supplier_id = selectedSupplier.id;
        }
        await updateItemMutation.mutateAsync({
          itemId: drawer.item.id,
          payload: updatePayload,
        });
      }
      finishClose();
    } catch (e) {
      setDrawerError(
        getOfficialActionError(
          e,
          "Não foi possível salvar o item. Revise os dados e tente novamente.",
        ),
      );
    } finally {
      itemSubmitInFlightRef.current = false;
    }
  }

  async function handleDelete() {
    if (!itemsActionsReady || !canRemoveQuotationItem || !drawer?.item) return;
    setDrawerError(null);
    try {
      await deleteItemMutation.mutateAsync(drawer.item.id);
      finishClose();
    } catch (e) {
      setDrawerError(
        getOfficialActionError(
          e,
          "Não foi possível excluir o item. Tente novamente.",
        ),
      );
    }
  }

  async function handleSelectSubmission(
    submission: SupplierQuotationSubmission,
  ) {
    if (!itemsActionsReady) return;
    const key = `${submission.product_id}:${submission.supplier_id}`;
    // Optimistically mark as selected so the button disappears immediately
    // and double-clicks are ignored.
    if (effectiveQuotedKeys.has(key)) return;
    setActionError(null);
    setPendingSelections((prev) => new Set([...prev, key]));
    try {
      await createItemMutation.mutateAsync(
        buildCreateQuotationItemPayloadFromSubmission(submission),
      );
    } catch (e) {
      // Roll back the optimistic update so the button reappears.
      setPendingSelections((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      const message = getOfficialActionError(
        e,
        "Não foi possível selecionar a cotação do fornecedor. Tente novamente.",
      );
      setActionError(message);
      console.error(message);
    }
  }

  async function handleRemoveSelectedItem(item: EnrichedQuotationItem) {
    if (!itemsActionsReady) return;
    const stableKey = `${item.product_id}:${item.supplier_id}`;
    setActionError(null);

    // 1. Start exit animation — item stays visible in list
    setExitingIds((prev) => new Set([...prev, stableKey]));

    // 2. Wait for animation to finish before hiding and firing API
    await new Promise((res) => setTimeout(res, EXIT_ANIMATION_MS));

    // Guard: bail out if component unmounted during animation
    if (!isMountedRef.current) return;
    if (!itemsActionsReadyRef.current) {
      setExitingIds((prev) => {
        const next = new Set(prev);
        next.delete(stableKey);
        return next;
      });
      return;
    }

    // 3. Hide optimistically, then fire the API call
    setPendingRemovals((prev) => new Set([...prev, item.id]));
    setPendingDeselections((prev) => new Set([...prev, stableKey]));
    setExitingIds((prev) => {
      const next = new Set(prev);
      next.delete(stableKey);
      return next;
    });
    try {
      await deleteItemMutation.mutateAsync(item.id);
      setPendingRemovals((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    } catch (e) {
      // Rollback: item is still in allItems (API failed), remove from both sets
      setPendingRemovals((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      setPendingDeselections((prev) => {
        const next = new Set(prev);
        next.delete(stableKey);
        return next;
      });
      setExitingIds((prev) => {
        const next = new Set(prev);
        next.delete(stableKey);
        return next;
      });
      const message = getOfficialActionError(
        e,
        "Não foi possível remover o item selecionado. Tente novamente.",
      );
      setActionError(message);
      console.error(message);
    }
  }

  async function handleToggleOffer(item: EnrichedQuotationItem) {
    if (!itemsActionsReady) return;
    setActionError(null);
    try {
      await updateItemMutation.mutateAsync({
        itemId: item.id,
        payload: { is_offer: !item.is_offer },
      });
    } catch (error) {
      setActionError(
        getOfficialActionError(
          error,
          "Não foi possível atualizar a oferta. Tente novamente.",
        ),
      );
    }
  }

  async function clearOffers() {
    if (!itemsActionsReady) return;
    setActionError(null);
    try {
      await Promise.all(
        effectiveItems
          .filter((item) => item.is_offer)
          .map((item) =>
            updateItemMutation.mutateAsync({
              itemId: item.id,
              payload: { is_offer: false },
            }),
          ),
      );
    } catch (error) {
      setActionError(
        getOfficialActionError(
          error,
          "Não foi possível atualizar as ofertas. Tente novamente.",
        ),
      );
    }
  }

  const isPending =
    createItemMutation.isPending ||
    updateItemMutation.isPending ||
    deleteItemMutation.isPending ||
    createProductMutation.isPending ||
    createSupplierMutation.isPending ||
    updateQuotationMutation.isPending;

  const allItems = itemsData?.items ?? [];
  const allSubmissions = submissionsData?.items ?? [];
  useEffect(() => {
    const reconciled = reconcileOptimisticState(
      allItems,
      pendingSelections,
      pendingRemovals,
      pendingDeselections,
      exitingIds,
    );

    setPendingSelections((prev) =>
      prev.size === reconciled.pendingSelections.size &&
      [...prev].every((key) => reconciled.pendingSelections.has(key))
        ? prev
        : reconciled.pendingSelections,
    );
    setPendingRemovals((prev) =>
      prev.size === reconciled.pendingRemovals.size &&
      [...prev].every((itemId) => reconciled.pendingRemovals.has(itemId))
        ? prev
        : reconciled.pendingRemovals,
    );
    setPendingDeselections((prev) =>
      prev.size === reconciled.pendingDeselections.size &&
      [...prev].every((key) => reconciled.pendingDeselections.has(key))
        ? prev
        : reconciled.pendingDeselections,
    );
    setExitingIds((prev) =>
      prev.size === reconciled.exitingIds.size &&
      [...prev].every((key) => reconciled.exitingIds.has(key))
        ? prev
        : reconciled.exitingIds,
    );
  }, [
    allItems,
    pendingSelections,
    pendingRemovals,
    pendingDeselections,
    exitingIds,
  ]);
  const effectiveItems = useMemo(
    () => buildEffectiveItems(allItems, pendingRemovals, pendingDeselections),
    [allItems, pendingRemovals, pendingDeselections],
  );
  const effectiveQuotedKeys = useMemo(
    () => buildEffectiveQuotedKeys(effectiveItems, pendingSelections),
    [effectiveItems, pendingSelections],
  );
  const canExportQuotation =
    canManageQuotation &&
    (!isSuper || featureFlags?.feature_quotation_export_report === true) &&
    !!quotation;
  const normalizedSearch = searchQuery.trim().toLocaleLowerCase("pt-BR");
  const itemBySubmissionKey = new Map(
    effectiveItems.map((item) => [
      `${item.product_id}:${item.supplier_id}`,
      item,
    ]),
  );
  const filteredItems = effectiveItems.filter(
    (item) =>
      (!normalizedSearch ||
        item.product_name
          .toLocaleLowerCase("pt-BR")
          .includes(normalizedSearch) ||
        item.supplier_name
          .toLocaleLowerCase("pt-BR")
          .includes(normalizedSearch)) &&
      matchesQuotationDetailFilters(
        {
          selected: true,
          isOffer: item.is_offer,
          category: item.product_category,
          supplierId: item.supplier_id,
          price: isDirectKgUnit(item.unit_type)
            ? (item.price_per_kg ?? null)
            : (item.price_per_box ?? null),
        },
        filters,
      ),
  );
  const filteredSubmissions = allSubmissions.filter((submission) => {
    const selectedItem = itemBySubmissionKey.get(
      `${submission.product_id}:${submission.supplier_id}`,
    );
    const selected = effectiveQuotedKeys.has(
      `${submission.product_id}:${submission.supplier_id}`,
    );
    const displayItem = selectedItem ?? submission;
    return (
      (!normalizedSearch ||
        displayItem.product_name
          .toLocaleLowerCase("pt-BR")
          .includes(normalizedSearch) ||
        displayItem.supplier_name
          .toLocaleLowerCase("pt-BR")
          .includes(normalizedSearch)) &&
      matchesQuotationDetailFilters(
        {
          selected,
          isOffer: selectedItem?.is_offer ?? false,
          category: displayItem.product_category,
          supplierId: displayItem.supplier_id,
          price: isDirectKgUnit(displayItem.unit_type as UnitType)
            ? (displayItem.price_per_kg ?? null)
            : (displayItem.price_per_box ?? null),
        },
        filters,
      )
    );
  });

  const grouped = buildStableGrouped(
    filteredItems,
    filteredSubmissions,
    effectiveQuotedKeys,
    exitingIds,
  );

  const isCompleted = quotation?.status === "completed";
  const categoryFilterOptions = CATEGORY_ORDER.map((category) => ({
    value: category,
    label: CATEGORY_LABELS[category] ?? category,
  }));
  const supplierFilterOptions = (suppliersData?.items ?? []).map(
    (supplier) => ({ value: supplier.id, label: supplier.name }),
  );

  return (
    <>
      <style>{`
        @keyframes fadeSlideIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes cardExit { from { opacity: 1; max-height: 200px; } to { opacity: 0; max-height: 0; } }
        .item-card-exiting { animation: cardExit 0.2s ease forwards; overflow: hidden; pointer-events: none; }
        .btn-add { transition: opacity 0.15s, transform 0.15s; }
        .btn-add:hover { opacity: 0.85; transform: scale(1.02); }
        .btn-primary { transition: opacity 0.15s; }
        .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .btn-primary:hover:not(:disabled) { opacity: 0.85; }
        .btn-danger { transition: opacity 0.15s, background 0.15s; }
        .btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }
        .btn-danger:hover:not(:disabled) { background: rgba(224,83,83,0.1) !important; }
        .cat-header { transition: filter 0.15s; }
        .cat-header:hover { filter: brightness(0.95); }
        .dropdown-item { transition: background 0.12s; cursor: pointer; padding: 0.5rem 0.75rem; }
        .dropdown-item:hover { background: var(--login-bg); }
        .unit-toggle button { transition: background 0.15s, color 0.15s; }
        .autocomplete-input:focus { border-color: var(--login-accent) !important; }
        
        /* Slick Search Bar UI */
        .search-container {
          position: relative;
          width: 100%;
          margin-bottom: 1.25rem;
        }
        .search-input {
          width: 100%;
          padding: 0.75rem 2.5rem 0.75rem 2.5rem;
          background: var(--login-surface);
          border: 1px solid var(--login-border);
          border-radius: 0.625rem;
          color: var(--login-text);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9375rem;
          outline: none;
          box-sizing: border-box;
          transition: border-color 0.2s ease, background 0.2s ease;
        }
        .search-input:focus {
          border-color: var(--login-accent);
          background: var(--login-bg);
        }
        .search-input::placeholder {
          color: var(--login-muted);
          opacity: 0.55;
        }
        .search-clear-btn {
          position: absolute;
          right: 0.875rem;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          color: var(--login-muted);
          cursor: pointer;
          display: flex;
          align-items: center;
          padding: 0.125rem;
          transition: color 0.15s;
        }
        .search-clear-btn:hover { color: var(--login-text); }

        /* Floating Stack Styles */
        .floating-stack {
          position: sticky;
          bottom: 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          z-index: 50;
          pointer-events: none;
          margin-top: 2rem;
        }
        @media (max-width: 768px) {
          .floating-stack {
            bottom: 5.5rem;
          }
        }
        .floating-pill {
          pointer-events: auto;
          display: flex;
          gap: 0.375rem;
          background: var(--login-surface);
          padding: 0.375rem;
          border-radius: 9999px;
          box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0,0,0,0.04);
          border: 1px solid var(--login-border);
          transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease;
          width: max-content;
          max-width: 92vw;
        }
        .floating-pill:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0,0,0,0.06);
        }
        .btn-pill {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.375rem;
          padding: 0.625rem 1.25rem;
          border-radius: 9999px;
          font-family: 'Barlow Condensed', sans-serif;
          font-weight: 700;
          font-size: 0.9375rem;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          cursor: pointer;
          white-space: nowrap;
          border: none;
          transition: all 0.2s ease;
        }
        @media (max-width: 400px) {
          .btn-pill {
            padding: 0.5rem 0.875rem;
            font-size: 0.8125rem;
          }
        }
        @media (max-width: 340px) {
          .btn-pill {
            padding: 0.4rem 0.6rem;
            font-size: 0.7rem;
            gap: 0.25rem;
          }
        }
        .btn-pill:disabled { opacity: 0.6; cursor: not-allowed; }
        .btn-pill-success { background: #16a34a; color: white; }
        .btn-pill-success:hover:not(:disabled) { background: #15803d; }
        .btn-pill-accent { background: var(--login-accent); color: white; }
        .btn-pill-accent:hover:not(:disabled) { filter: brightness(0.9); }
        .btn-pill-outline { background: transparent; color: var(--login-text); border: 1px solid var(--login-border); }
        .btn-pill-outline:hover:not(:disabled) { background: var(--login-bg); }
        .btn-pill-reopen {
          background: color-mix(in srgb, var(--login-accent) 18%, var(--login-surface));
          color: color-mix(in srgb, var(--login-accent) 78%, white);
          border: 1px solid color-mix(in srgb, var(--login-accent) 52%, var(--login-border));
        }
        .btn-pill-reopen:hover:not(:disabled) {
          background: color-mix(in srgb, var(--login-accent) 26%, var(--login-surface));
          border-color: color-mix(in srgb, var(--login-accent) 68%, var(--login-border));
        }
      `}</style>

      <div className="quotation-detail-page lnk-page lnk-super-page app-page-frame">
        <QuotationDetailHeader
          quotation={quotation}
          topSlot={topSlot}
          canExportQuotation={canExportQuotation}
          onExport={() => setExportModalOpen(true)}
          selectedCount={
            itemsReady
              ? new Set(effectiveItems.map((item) => item.product_id)).size
              : undefined
          }
          offerCount={
            itemsReady
              ? effectiveItems.filter((item) => item.is_offer).length
              : undefined
          }
          canConclude={canManageQuotation}
          onReopen={canManageQuotation ? handleReopen : undefined}
          reopenPending={updateQuotationMutation.isPending}
        />

        <div className="quotation-detail-toolbar lnk-admin-filters">
          <label className="search-container">
            <Search size={17} />
            <input
              type="search"
              aria-label="Buscar por produto ou fornecedor"
              className="search-input"
              placeholder="Buscar produto ou fornecedor"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                aria-label="Limpar busca"
                className="search-clear-btn"
                onClick={() => setSearchQuery("")}
              >
                <X size={16} strokeWidth={2} />
              </button>
            )}
          </label>
          <QuotationDetailFilterPopover
            mode="official"
            filters={filters}
            categories={categoryFilterOptions}
            suppliers={supplierFilterOptions}
            onApply={setFilters}
            onClear={() => setFilters(EMPTY_QUOTATION_DETAIL_FILTERS)}
          />
          {!isCompleted && canManageQuotation ? (
            <button
              key="add-item"
              type="button"
              className="lnk-add-item"
              disabled={!itemsActionsReady}
              onClick={openCreate}
            >
              <Plus size={16} /> Adicionar item
            </button>
          ) : null}
        </div>

        {(itemsLoading || (canManageQuotation && submissionsLoading)) && (
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              color: "var(--login-muted)",
              fontSize: "0.875rem",
            }}
          >
            Carregando...
          </p>
        )}

        {itemsQuery.isError && (
          <InlineLoadError
            title="Itens indisponíveis"
            message="Não foi possível carregar os itens da cotação."
            onRetry={() => void itemsQuery.refetch()}
            isRetrying={itemsQuery.isFetching}
          />
        )}
        {itemsReady && submissionsQuery.isError && (
          <InlineLoadError
            title="Listas indisponíveis"
            message="Não foi possível carregar as listas dos fornecedores."
            onRetry={() => void submissionsQuery.refetch()}
            isRetrying={submissionsQuery.isFetching}
          />
        )}

        {actionError && (
          <div
            style={{
              marginBottom: "1rem",
              padding: "0.75rem 0.875rem",
              borderRadius: "0.5rem",
              border:
                "1px solid color-mix(in srgb, var(--login-error, #e05353) 55%, var(--login-border))",
              background:
                "color-mix(in srgb, var(--login-error, #e05353) 8%, var(--login-surface))",
              color: "var(--login-error, #e05353)",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "0.875rem",
            }}
          >
            {actionError}
          </div>
        )}

        {/* Empty States */}
        {ledgerReady &&
          effectiveItems.length === 0 &&
          filteredSubmissions.length === 0 && (
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                color: "var(--login-muted)",
                fontSize: "0.875rem",
                marginBottom: "1.5rem",
              }}
            >
              Nenhum preço enviado pelos fornecedores para esta data.
            </p>
          )}

        {ledgerReady &&
          effectiveItems.length > 0 &&
          filteredItems.length === 0 && (
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                color: "var(--login-muted)",
                fontSize: "0.875rem",
                marginBottom: "1.5rem",
              }}
            >
              Nenhum item encontrado para "{searchQuery}".
            </p>
          )}

        <section className="lnk-catalog-section">
          <div className="lnk-section-heading quotation-official-heading">
            <div>
              <span className="lnk-kicker">LISTAS DOS FORNECEDORES</span>
              <h2>Selecionar itens oficiais</h2>
            </div>
            {ledgerReady &&
            canManageQuotation &&
            !isCompleted &&
            effectiveItems.some((item) => item.is_offer) ? (
              <button
                type="button"
                className="lnk-clear-offers"
                disabled={updateItemMutation.isPending || !itemsActionsReady}
                onClick={() => void clearOffers()}
              >
                <BadgePercent size={15} /> Desmarcar todas as ofertas{" "}
                <b>{effectiveItems.filter((item) => item.is_offer).length}</b>
              </button>
            ) : null}
          </div>
          <div className="lnk-admin-catalog-grid">
            {ledgerReady &&
              CATEGORY_ORDER.filter(
                (cat) => (grouped[cat]?.length ?? 0) > 0,
              ).map((cat) => {
                const catItems = grouped[cat]!;
                const expanded = expandedCategories.has(cat);
                return (
                  <section className="lnk-admin-category" key={cat}>
                    <button
                      className="lnk-category-heading"
                      aria-expanded={expanded}
                      onClick={() => toggleCategory(cat)}
                    >
                      <h3>{CATEGORY_LABELS[cat] ?? cat}</h3>
                    </button>

                    {expanded && (
                      <div className="lnk-admin-ledger">
                        {catItems.map((item) => {
                          const stableKey =
                            "isSuggestion" in item
                              ? `${item.submission.product_id}:${item.submission.supplier_id}`
                              : `${item.product_id}:${item.supplier_id}`;
                          const isExiting = exitingIds.has(stableKey);

                          if ("isSuggestion" in item) {
                            return (
                              <QuotationDetailLedgerRow
                                key={stableKey}
                                item={item}
                                selected={false}
                                source={`Lista · ${item.supplier_name}`}
                                actions={
                                  isCompleted ? null : (
                                    <button
                                      type="button"
                                      className="select"
                                      disabled={
                                        isCompleted ||
                                        createItemMutation.isPending ||
                                        !itemsActionsReady ||
                                        !canManageQuotation
                                      }
                                      onClick={() =>
                                        void handleSelectSubmission(
                                          item.submission,
                                        )
                                      }
                                    >
                                      <Plus size={16} /> Selecionar
                                    </button>
                                  )
                                }
                              />
                            );
                          }

                          const isOptimisticConfirmed =
                            item.isOptimistic === true;
                          const isRemoveDisabled =
                            isOptimisticConfirmed ||
                            isExiting ||
                            deleteItemMutation.isPending;
                          const canEditSelectedItem =
                            itemsActionsReady &&
                            canManageQuotation &&
                            !isCompleted &&
                            !isOptimisticConfirmed;
                          return (
                            <div
                              key={stableKey}
                              className={
                                isExiting ? "item-card-exiting" : undefined
                              }
                            >
                              <QuotationDetailLedgerRow
                                item={item}
                                selected
                                onEdit={
                                  isCompleted ? undefined : () => openEdit(item)
                                }
                                editDisabled={!canEditSelectedItem}
                                actions={
                                  isCompleted ? null : confirmRemoveId ===
                                    item.id ? (
                                    <QuotationItemRemoveConfirmation
                                      name={item.product_name}
                                      pending={
                                        isRemoveDisabled || !itemsActionsReady
                                      }
                                      confirmRef={confirmRemoveRef}
                                      onConfirm={() => {
                                        lastRemoveIdRef.current = null;
                                        setConfirmRemoveId(null);
                                        void handleRemoveSelectedItem(item);
                                      }}
                                      onCancel={() => setConfirmRemoveId(null)}
                                    />
                                  ) : (
                                    <>
                                      {!isOptimisticConfirmed &&
                                      canManageQuotation ? (
                                        <QuotationOfferButton
                                          active={item.is_offer}
                                          disabled={
                                            isCompleted ||
                                            updateItemMutation.isPending ||
                                            !itemsActionsReady
                                          }
                                          onClick={() =>
                                            void handleToggleOffer(item)
                                          }
                                        />
                                      ) : null}
                                      {canManageQuotation ? (
                                        <button
                                          type="button"
                                          className="edit"
                                          aria-label={`Editar ${item.product_name}`}
                                          disabled={!canEditSelectedItem}
                                          onClick={() => openEdit(item)}
                                        >
                                          <Pencil size={16} /> Editar
                                        </button>
                                      ) : null}
                                      {canRemoveQuotationItem &&
                                      quotation?.status === "in_progress" &&
                                      !isOptimisticConfirmed ? (
                                        <button
                                          ref={
                                            lastRemoveIdRef.current === item.id
                                              ? removeTriggerRef
                                              : undefined
                                          }
                                          type="button"
                                          className="delete"
                                          aria-label={`Remover ${item.product_name}`}
                                          disabled={
                                            isRemoveDisabled ||
                                            !itemsActionsReady
                                          }
                                          onClick={(event) => {
                                            lastRemoveIdRef.current = item.id;
                                            setConfirmRemoveId(item.id);
                                          }}
                                        >
                                          <Trash2 size={16} />{" "}
                                          <span className="quotation-delete-label">
                                            Excluir
                                          </span>
                                        </button>
                                      ) : null}
                                    </>
                                  )
                                }
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </section>
                );
              })}
          </div>
        </section>

        <QuotationItemEditor
          open={drawer !== null}
          onClose={closeDrawer}
          mode={drawer?.mode ?? "create"}
          hasProduct={!!selectedProduct}
          pending={isPending}
          onSubmit={() => {
            if (!itemsActionsReady) return;
            if (showInlineProduct) {
              void handleCreateInlineProduct();
              return;
            }

            if (showInlineSupplier) {
              void handleCreateInlineSupplier();
              return;
            }

            void handleSubmit();
          }}
        >
          <QuotationItemDrawerFields
            productField={
              <QuotationAutocompleteField
                label="Produto"
                value={productSearch}
                placeholder="Buscar produto..."
                selected={!!selectedProduct}
                readOnly={drawer?.mode === "edit"}
                showDropdown={showProductDropdown}
                hideDropdown={showInlineProduct}
                options={filteredProducts}
                getKey={(product) => product.id}
                getLabel={(product) => product.name}
                getMeta={(product) => product.category}
                createLabel="+ Novo produto"
                onValueChange={(value) => {
                  setProductSearch(value);
                  setSelectedProduct(null);
                  setShowProductDropdown(true);
                }}
                onClear={() => {
                  setProductSearch("");
                  setSelectedProduct(null);
                }}
                onFocus={() => setShowProductDropdown(true)}
                onDismiss={() => setShowProductDropdown(false)}
                onBlur={() => setShowProductDropdown(false)}
                onSelect={(product) => {
                  setSelectedProduct(product);
                  setProductSearch(product.name);
                  setShowProductDropdown(false);
                }}
                onCreateNew={() => {
                  setShowProductDropdown(false);
                  setShowInlineProduct(true);
                  setInlineProductName(productSearch);
                }}
              />
            }
            inlineProductPanel={
              showInlineProduct ? (
                <InlineCreateProductPanel
                  name={inlineProductName}
                  category={inlineProductCategory}
                  unit={inlineProductUnit}
                  categoryLabels={CATEGORY_LABELS}
                  pending={createProductMutation.isPending}
                  onNameChange={setInlineProductName}
                  onCategoryChange={setInlineProductCategory}
                  onUnitChange={setInlineProductUnit}
                  onCreate={handleCreateInlineProduct}
                  onCancel={() => setShowInlineProduct(false)}
                />
              ) : null
            }
            supplierField={
              <QuotationAutocompleteField
                label="Fornecedor"
                value={supplierSearch}
                placeholder="Buscar fornecedor..."
                selected={!!selectedSupplier}
                showDropdown={showSupplierDropdown}
                hideDropdown={showInlineSupplier}
                options={filteredSuppliers}
                getKey={(supplier) => supplier.id}
                getLabel={(supplier) => supplier.name}
                createLabel="+ Novo fornecedor"
                onValueChange={(value) => {
                  setSupplierSearch(value);
                  setSelectedSupplier(null);
                  setShowSupplierDropdown(true);
                }}
                onClear={() => {
                  setSupplierSearch("");
                  setSelectedSupplier(null);
                }}
                onFocus={() => setShowSupplierDropdown(true)}
                onDismiss={() => setShowSupplierDropdown(false)}
                onBlur={() => setShowSupplierDropdown(false)}
                onSelect={(supplier) => {
                  setSelectedSupplier(supplier);
                  setSupplierSearch(supplier.name);
                  setShowSupplierDropdown(false);
                }}
                onCreateNew={() => {
                  setShowSupplierDropdown(false);
                  setShowInlineSupplier(true);
                  setInlineSupplierName(supplierSearch);
                }}
              />
            }
            supplierWarning={
              drawer?.mode === "edit" &&
              selectedSupplier &&
              selectedSupplier.id !== drawer.item?.supplier_id ? (
                <QuotationSupplierChangeWarning />
              ) : null
            }
            inlineSupplierPanel={
              showInlineSupplier ? (
                <InlineCreateSupplierPanel
                  name={inlineSupplierName}
                  pending={createSupplierMutation.isPending}
                  onNameChange={setInlineSupplierName}
                  onCreate={handleCreateInlineSupplier}
                  onCancel={() => setShowInlineSupplier(false)}
                />
              ) : null
            }
            priceSection={
              <QuotationPriceSection
                unitType={unitType}
                pricePerKg={pricePerKg}
                pricePerBox={pricePerBox}
                onPricePerKgChange={setPricePerKg}
                onPricePerBoxChange={setPricePerBox}
              />
            }
            unitSection={
              <QuotationUnitSection
                unitType={unitType}
                kgPerBox={kgPerBox}
                unitsPerBox={unitsPerBox}
                onUnitTypeChange={applyUnitType}
                onKgPerBoxChange={setKgPerBox}
                onUnitsPerBoxChange={setUnitsPerBox}
              />
            }
            pricePreview={<QuotationPricePreview pricePreview={pricePreview} />}
            offerField={
              <button
                type="button"
                className={`lnk-editor-offer-toggle ${isOffer ? "is-active" : ""}`}
                aria-pressed={isOffer}
                onClick={() => setIsOffer((current) => !current)}
              >
                <span>
                  <BadgePercent size={18} />
                  <strong>Oferta para clientes</strong>
                </span>
                <i>{isOffer ? "Sim" : "Não"}</i>
              </button>
            }
          />

          {itemValidationError &&
            (validationAttempted ||
              !!pricePerBox ||
              !!pricePerKg ||
              !!kgPerBox ||
              !!unitsPerBox) && (
              <div
                role="alert"
                style={{
                  color: "var(--color-error, #ef4444)",
                  fontSize: "0.875rem",
                  marginTop: "0.5rem",
                }}
              >
                {itemValidationError}
              </div>
            )}
          {drawerError && (
            <div
              style={{
                color: "var(--color-error, #ef4444)",
                fontSize: "0.875rem",
                marginBottom: "0.5rem",
              }}
            >
              {drawerError}
            </div>
          )}

          <QuotationDrawerFooter
            mode={drawer?.mode ?? "create"}
            confirmDelete={confirmDelete}
            isPending={isPending}
            canSubmit={
              itemsActionsReady &&
              !!selectedProduct &&
              !!selectedSupplier &&
              !itemValidationError
            }
            canDelete={canRemoveQuotationItem}
            onRequestDelete={() => setConfirmDelete(true)}
            onConfirmDelete={handleDelete}
            onSubmit={handleSubmit}
            onClose={closeDrawer}
          />
        </QuotationItemEditor>
      </div>
      {exportModalOpen && quotation && (
        <QuotationExportModal
          quotation={quotation}
          onClose={() => setExportModalOpen(false)}
        />
      )}
    </>
  );
}
